import { useLayoutEffect, useRef } from "react";

/**
 * Keeps dropdown panels within the viewport.
 *
 * Dropdowns are positioned with `position: fixed` so they are not clipped by
 * ancestors with `overflow: hidden` (e.g. Doors tab table cards) when flipped
 * above the trigger after scrolling.
 *
 * Horizontal: nudge so both edges stay inside the viewport (8px margin). On
 * narrow screens, align the dropdown's right edge with the trigger.
 *
 * Vertical: cap height to available space; flip above the trigger when there is
 * more room there than below.
 */
export function useDropdownClamp<T extends HTMLElement>(open: boolean) {
    const ref = useRef<T>(null);

    useLayoutEffect(() => {
        if (!open) return;
        const el = ref.current;
        if (!el) return;

        const margin = 8;
        const gap = 4;
        const maxCap = 400;

        const clamp = () => {
            const trigger = el.previousElementSibling as HTMLElement | null;
            if (!trigger) return;

            const vw = document.documentElement.clientWidth;
            const vh = document.documentElement.clientHeight;
            const tr = trigger.getBoundingClientRect();
            const spaceBelow = vh - tr.bottom - margin;
            const spaceAbove = tr.top - margin;
            const alignRight = vw <= 768;

            el.style.position = "fixed";
            el.style.transform = "none";
            el.style.marginTop = "0";
            el.style.marginBottom = "0";
            el.style.minWidth = `${tr.width}px`;
            el.style.width = "max-content";
            el.style.maxWidth = `${Math.min(maxCap, vw - 2 * margin)}px`;
            el.style.zIndex = "1000";

            // Measure natural height with a temporary below-trigger placement.
            el.style.top = `${tr.bottom + gap}px`;
            el.style.bottom = "auto";
            el.style.maxHeight = `${maxCap}px`;
            if (alignRight) {
                el.style.left = "auto";
                el.style.right = `${vw - tr.right}px`;
            } else {
                el.style.right = "auto";
                el.style.left = `${tr.left}px`;
            }

            const needed = el.scrollHeight;
            const placeAbove = needed > spaceBelow && spaceAbove > spaceBelow;

            if (placeAbove) {
                el.style.top = "auto";
                el.style.bottom = `${vh - tr.top + gap}px`;
                el.style.maxHeight = `${Math.min(maxCap, Math.max(0, spaceAbove))}px`;
            } else {
                el.style.top = `${tr.bottom + gap}px`;
                el.style.bottom = "auto";
                el.style.maxHeight = `${Math.min(maxCap, Math.max(0, spaceBelow))}px`;
            }

            // Horizontal clamp after final vertical placement.
            const rect = el.getBoundingClientRect();
            if (alignRight) {
                if (rect.left < margin) {
                    el.style.right = `${Math.max(margin, vw - tr.right - (margin - rect.left))}px`;
                }
            } else {
                let left = tr.left;
                if (rect.right > vw - margin) left -= rect.right - (vw - margin);
                if (left < margin) left = margin;
                el.style.left = `${left}px`;
            }
        };

        clamp();
        window.addEventListener("resize", clamp);
        window.addEventListener("scroll", clamp, true);
        return () => {
            window.removeEventListener("resize", clamp);
            window.removeEventListener("scroll", clamp, true);
        };
    }, [open]);

    return ref;
}
